import parse from 'html-react-parser'
import React from 'react'

/**
 * Represents a WordPress block.
 */
export class Block {
    constructor(block, includeChildren = false) {
        this.name = block.name
        this.html = this.setHTML(block)
        this.rootElement = this.parseHTML(this.html)
        this.children = this.getChildrenHTML(block.innerBlocks)
        includeChildren && this.includeChildren()
        this.block = block
    }

    /**
     * Depending on the block, the html to render can be stored in a
     * different property.
     *
     * @param {object} block The block object from GraphQL query.
     * @returns string The HTML property.
     */
    setHTML(block) {
        return block.isDynamic
            ? block.dynamicContent
            : block.saveContent
            ? block.saveContent
            : block.originalContent
            ? block.originalContent
            : ''
    }

    /* TODO: function currently not used */
    getChildrenHTML(children) {
        if (!children || !children.length > 0) {
            return ''
        }
        const html = children.reduce((html, block) => {
            return `${html}${this.setHTML(block)}${this.getChildrenHTML(
                block.innerBlocks,
            )}`
        }, '')

        return this.parseHTML(html)
    }

    /* TODO: function currently not used */
    includeChildren() {
        this.rootElement = React.cloneElement(
            this.rootElement,
            [],
            this.children,
        )
    }

    /**
     * The parser converts an HTML string to one or more React elements.
     *
     * @see https://www.npmjs.com/package/html-react-parser
     *
     * @param {string} html The html to parse.
     * @param {boolean} trim Whether to remove whitespace from html.
     * @returns object React component
     */
    parseHTML(html, trim = true) {
        return parse(html, { trim })
    }

    /**
     * Get the html string.
     *
     * @returns string
     */
    getHTML() {
        return this.html
    }

    /**
     * Get the root element. This can be rendered in another React component.
     * @returns a React Component object.
     */
    getRootElement() {
        return this.rootElement
    }

    /**
     * Set a child element as the root.
     *
     * Note, it will take the last child element that matches in the html.
     *
     * @param {string} type Element type.
     * @param {*} attributes The attributes to match
     * @returns A React Element
     */
    setElementAsRoot(type, attributes) {
        if (this.rootElement.props?.children) {
            React.Children.forEach(this.rootElement.props.children, child => {
                if (!attributes)
                    if (
                        child.type === type &&
                        (!attributes || this.matchAttributes(child, attributes))
                    ) {
                        this.rootElement = child
                    }
            })
        }
    }

    /**
     * Retrieve specific child elements from the root element.
     *
     * @param {object} rootElement The root element of the block.
     * @param {string} type Element type (div, span, etc...).
     * @param {object} attributes Element attributes to match.
     * @returns An array of elements that match the type and attributes
     */
    getElements(rootElement, type, attributes) {
        let elements = []
        if (rootElement.type == type) {
            if (attributes) {
                this.matchAttributes(rootElement, attributes) &&
                    elements.push(rootElement)
            } else {
                elements.push(rootElement)
            }
        }

        if (rootElement.props?.children) {
            React.Children.forEach(rootElement.props.children, child => {
                const childElements = this.getElements(child, type, attributes)
                elements = [...elements, ...childElements]
            })
        }

        return elements
    }

    /**
     * Matches an element to attribute properties.
     *
     * @param {object} element A React element
     * @param {object} attributes An object of element attributes (props) to match
     * @returns Boolean on whether the element matches the attributes
     */
    matchAttributes(element, attributes) {
        if (typeof attributes != 'object') return false

        const matches = Object.keys(attributes).map(property => {
            if (typeof element.props?.[property] == 'undefined') return false

            let attribute = attributes[property]
            let match = attribute?.match ?? 'equal'

            if (match == 'includes') {
                return element.props[property].includes(attribute.value)
            } else {
                return attribute.value == element.props[property]
            }
        })

        return !matches.includes(false)
    }

    /**
     * Removes duplicate objects based on a single property.
     *
     * @param {array} array An array of objects
     * @param {string} property A property of the objects to find duplicates
     * @returns A deduped object
     */
    removeDuplicateObjectsByProperty(array, property) {
        return Array.isArray(array)
            ? [...new Map(array.map(m => [m[property], m])).values()]
            : array
    }
}
